import React, { useMemo } from "react";
import { Badge, Box, IconButton, Tooltip, alpha, useTheme } from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AllInboxOutlinedIcon from "@mui/icons-material/AllInboxOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  isPopupTeamContext,
  teamTasksPaths,
  teamAgendaPaths,
  teamSalesPipelinePaths,
  teamAwarenessPipelinePaths,
  teamVirtualOfficePaths,
  chatPathWithWorkspaceNav,
  serviceChatPath,
} from "../../../utils/teamNavigation";
import { openTeamTasksPopupWindow } from "../../../utils/openTeamTasksPopupWindow";
import { openTeamPopupWindow } from "../../../utils/openTeamPopupWindow";
import { openSalesPipelinePopupWindow } from "../../../utils/openSalesPipelinePopupWindow";
import { openAwarenessPipelinePopupWindow } from "../../../utils/openAwarenessPipelinePopupWindow";
import { useMobileVisualViewport } from "../../../hooks/useMobileVisualViewport";
import {
  TEAM_MOBILE_RAIL_BUTTON_SIZE_PX,
  TEAM_MOBILE_RAIL_ICON_SIZE_PX,
} from "../utils/teamUiUtils";
import { useWorkspaceRailCounts } from "../../../hooks/useWorkspaceRailCounts";
import { useAuthContext } from "../../../context/AuthContext";
import {
  useEffectivePermissions,
  can,
  scopeSetFromPermissions,
} from "../../../hooks/useEffectivePermissions";

function formatBadgeCount(count) {
  const value = Number(count) || 0;
  if (value <= 0) return 0;
  return value > 99 ? "99+" : value;
}

function RailButton({
  title,
  selected,
  onClick,
  children,
  badgeCount = 0,
  colorKey = "primary",
  tooltipPlacement = "left",
  mobile = false,
}) {
  const theme = useTheme();
  const paletteKey =
    colorKey === "secondary" ? "secondary" : colorKey === "info" ? "info" : "primary";
  const main = theme.palette[paletteKey]?.main || theme.palette.primary.main;
  const buttonSize = mobile ? TEAM_MOBILE_RAIL_BUTTON_SIZE_PX : 36;
  const badgeLabel = formatBadgeCount(badgeCount);
  const showBadge = badgeLabel !== 0;

  return (
    <Tooltip title={title} placement={tooltipPlacement}>
      <Badge
        color="error"
        badgeContent={badgeLabel}
        invisible={!showBadge}
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          overflow: "visible",
          "& .MuiBadge-badge": {
            top: mobile ? 2 : 4,
            right: mobile ? 2 : 4,
            zIndex: 2,
            fontWeight: 700,
            fontSize: mobile ? "0.6rem" : "0.65rem",
            minWidth: mobile ? 14 : 16,
            height: mobile ? 14 : 16,
            px: 0.5,
          },
        }}
      >
        <IconButton
          size={mobile ? "medium" : "small"}
          onClick={onClick}
          onAuxClick={onClick}
          sx={{
            width: buttonSize,
            height: buttonSize,
            borderRadius: mobile ? 2 : 1.5,
            color: selected ? main : "text.secondary",
            bgcolor: selected ? alpha(main, 0.14) : "transparent",
            "&:hover": {
              bgcolor: alpha(main, selected ? 0.2 : 0.08),
              color: main,
            },
          }}
        >
          {children}
        </IconButton>
      </Badge>
    </Tooltip>
  );
}

export default function TeamRightRail({
  basePath = "/equipe",
  activeSection = "chat",
  isMobile = false,
}) {
  const { t } = useTranslation("team");
  const { t: tApp } = useTranslation("app");
  const { t: tTasks } = useTranslation("teamTasks");
  const { t: tAgenda } = useTranslation("teamAgenda");
  const navigate = useNavigate();
  const theme = useTheme();
  const { keyboardOpen } = useMobileVisualViewport(isMobile);
  const railCounts = useWorkspaceRailCounts();
  const { userData } = useAuthContext();
  const isMasterUser = Number(userData?.master) === 1;
  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(
    () =>
      scopeSetFromPermissions(permData, {
        ...userData,
        master: isMasterUser ? 1 : 0,
      }),
    [permData, userData, isMasterUser]
  );
  const canVirtualOffice = can(scopeSet, "virtual_office", "view");
  const canAwareness = can(scopeSet, "awareness", "view");
  const tasksPaths = useMemo(() => teamTasksPaths(basePath), [basePath]);
  const agendaPaths = useMemo(() => teamAgendaPaths(basePath), [basePath]);
  const salesPipelinePaths = useMemo(() => teamSalesPipelinePaths(basePath), [basePath]);
  const awarenessPipelinePaths = useMemo(() => teamAwarenessPipelinePaths(basePath), [basePath]);
  const virtualOfficePaths = useMemo(() => teamVirtualOfficePaths(basePath), [basePath]);
  const showPopupActions = !isPopupTeamContext(basePath);

  const serviceTitle = tApp("nav.chat");

  const chatTitle = showPopupActions
    ? `${t("railChatNav")} (Ctrl+clique: popup)`
    : t("railChatNav");

  const inboxTitle = showPopupActions
    ? `${tTasks("inboxNavMine")} (Ctrl+clique: popup)`
    : tTasks("inboxNavMine");

  const tasksTitle = showPopupActions
    ? `${tTasks("tasksNav")} (Ctrl+clique: popup)`
    : tTasks("tasksNav");

  const salesPipelineTitle = showPopupActions
    ? `${tApp("nav.salesPipeline")} (Ctrl+clique: popup)`
    : tApp("nav.salesPipeline");

  const awarenessTitle = showPopupActions
    ? `${tApp("nav.awareness")} (Ctrl+clique: popup)`
    : tApp("nav.awareness");

  const handleServiceClick = (e) => {
    if (e.button === 0) {
      navigate(
        isPopupTeamContext(basePath)
          ? serviceChatPath(undefined, basePath)
          : chatPathWithWorkspaceNav()
      );
    }
  };

  const handleChatClick = (e) => {
    if (showPopupActions && (e.ctrlKey || e.metaKey || e.button === 1)) {
      e.preventDefault();
      openTeamPopupWindow();
      return;
    }
    if (e.button === 0) navigate(basePath);
  };

  const handleInboxClick = (e) => {
    if (showPopupActions && (e.ctrlKey || e.metaKey || e.button === 1)) {
      e.preventDefault();
      openTeamTasksPopupWindow({ inbox: true });
      return;
    }
    if (e.button === 0) navigate(tasksPaths.inbox);
  };

  const handleTasksClick = (e) => {
    if (showPopupActions && (e.ctrlKey || e.metaKey || e.button === 1)) {
      e.preventDefault();
      openTeamTasksPopupWindow();
      return;
    }
    if (e.button === 0) navigate(tasksPaths.list);
  };

  const handleAgendaClick = () => {
    navigate(agendaPaths.root);
  };

  const handleSalesPipelineClick = (e) => {
    if (showPopupActions && (e.ctrlKey || e.metaKey || e.button === 1)) {
      e.preventDefault();
      openSalesPipelinePopupWindow();
      return;
    }
    if (e.button === 0) navigate(salesPipelinePaths.root);
  };

  const handleAwarenessClick = (e) => {
    if (showPopupActions && (e.ctrlKey || e.metaKey || e.button === 1)) {
      e.preventDefault();
      openAwarenessPipelinePopupWindow();
      return;
    }
    if (e.button === 0) navigate(awarenessPipelinePaths.root);
  };

  const handleVirtualOfficeClick = () => {
    navigate(virtualOfficePaths.root);
  };

  const iconSize = isMobile ? TEAM_MOBILE_RAIL_ICON_SIZE_PX : 20;

  const items = useMemo(
    () => {
      const list = [
      {
        key: "service",
        title: serviceTitle,
        colorKey: "primary",
        selected: activeSection === "service",
        onClick: handleServiceClick,
        badgeCount: activeSection === "service" ? 0 : railCounts.service,
        icon: <AllInboxOutlinedIcon sx={{ fontSize: iconSize }} />,
      },
      {
        key: "inbox",
        title: inboxTitle,
        colorKey: "secondary",
        selected: activeSection === "inbox",
        onClick: handleInboxClick,
        badgeCount: activeSection === "inbox" ? 0 : railCounts.inbox,
        icon: <AssignmentOutlinedIcon sx={{ fontSize: iconSize }} />,
      },
      {
        key: "agenda",
        title: tAgenda("agendaNav"),
        colorKey: "info",
        selected: activeSection === "agenda",
        onClick: handleAgendaClick,
        badgeCount: activeSection === "agenda" ? 0 : railCounts.agenda,
        icon: <CalendarMonthOutlinedIcon sx={{ fontSize: iconSize }} />,
      },
      {
        key: "salesPipeline",
        title: salesPipelineTitle,
        colorKey: "primary",
        selected: activeSection === "salesPipeline",
        onClick: handleSalesPipelineClick,
        badgeCount: 0,
        icon: <TimelineOutlinedIcon sx={{ fontSize: iconSize }} />,
      },
      ...(canAwareness
        ? [
            {
              key: "awareness",
              title: awarenessTitle,
              colorKey: "primary",
              selected: activeSection === "awareness",
              onClick: handleAwarenessClick,
              badgeCount: 0,
              icon: <PsychologyOutlinedIcon sx={{ fontSize: iconSize }} />,
            },
          ]
        : []),
      {
        key: "tasks",
        title: tasksTitle,
        colorKey: "primary",
        selected: activeSection === "tasks",
        onClick: handleTasksClick,
        badgeCount: 0,
        icon: <ViewKanbanOutlinedIcon sx={{ fontSize: iconSize }} />,
      },
      {
        key: "chat",
        title: chatTitle,
        colorKey: "primary",
        selected: activeSection === "chat",
        onClick: handleChatClick,
        badgeCount: activeSection === "chat" ? 0 : railCounts.chat,
        icon: <ForumOutlinedIcon sx={{ fontSize: iconSize }} />,
      },
    ];
      if (canVirtualOffice) {
        list.unshift({
          key: "virtualOffice",
          title: "Escritório Virtual",
          colorKey: "info",
          selected: activeSection === "virtualOffice",
          onClick: handleVirtualOfficeClick,
          badgeCount: 0,
          icon: <DesktopWindowsOutlinedIcon sx={{ fontSize: iconSize }} />,
        });
      }
      return list;
    },
    [
      activeSection,
      awarenessTitle,
      canAwareness,
      canVirtualOffice,
      chatTitle,
      railCounts,
      handleAgendaClick,
      handleAwarenessClick,
      handleChatClick,
      handleInboxClick,
      handleSalesPipelineClick,
      handleServiceClick,
      handleTasksClick,
      handleVirtualOfficeClick,
      iconSize,
      inboxTitle,
      isMobile,
      salesPipelineTitle,
      serviceTitle,
      tAgenda,
      tasksTitle,
    ]
  );

  if (isMobile && keyboardOpen) {
    return null;
  }

  if (isMobile) {
    return (
      <Box
        component="nav"
        aria-label={t("railWorkspaceNav")}
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-around",
          width: "100%",
          px: 1.5,
          pt: 0.25,
          pb: "max(4px, env(safe-area-inset-bottom, 0px))",
          borderTop: 1,
          borderColor: "divider",
          bgcolor: theme.palette.background.paper,
        }}
      >
        {items.map((item) => (
          <RailButton
            key={item.key}
            title={item.title}
            colorKey={item.colorKey}
            selected={item.selected}
            onClick={item.onClick}
            badgeCount={item.badgeCount}
            tooltipPlacement="top"
            mobile
          >
            {item.icon}
          </RailButton>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 48,
        flexShrink: 0,
        alignSelf: "stretch",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: 1,
        borderColor: "divider",
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          py: 1.5,
          gap: 0.75,
        }}
      >
        {items.map((item) => (
          <RailButton
            key={item.key}
            title={item.title}
            colorKey={item.colorKey}
            selected={item.selected}
            onClick={item.onClick}
            badgeCount={item.badgeCount}
          >
            {item.icon}
          </RailButton>
        ))}
      </Box>
    </Box>
  );
}
